import { useEffect, useRef } from 'react';
import { Method } from 'axios';
import {
  DefaultError,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from '@tanstack/react-query';

import { useDialog, useApiContext } from 'src/compass/site/providers';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { onErrorDefaultHandler } from 'src/compass/shared/util/onErrorHandlers';
import { CasLoaderWithBackdrop, CasMutationErrorDialog } from 'src/compass/shared/components';

export interface IGenericMutationProps
  extends UseMutationOptions<unknown, DefaultError, any, unknown> {
  errorTitle?: string;
  /*
    Runs when the sender dismisses the error dialog - inside that click, so it may open a window.
    Optional; without it the dialog only closes.
  */
  onErrorDismiss?: (error: DefaultError) => void;
  showLoader?: boolean;
  includesAttachment?: boolean;
  params?: Record<string, any>;
}

export type GenericMutationResult = {
  mutation: UseMutationResult<unknown, DefaultError, Object, unknown>;
};

interface useCasMutationProps extends IGenericMutationProps {
  method: Method;
  url: string;
}

export default function useCasMutation({
  method,
  url,
  params,
  includesAttachment=false,
  onSuccess = onSuccessEnqueueSnackbar,
  onError,
  errorTitle,
  onErrorDismiss,
  showLoader = true,
  ...rest
}: useCasMutationProps) {
  const { api } = useApiContext();
  const dialog = useDialog();

  // DialogContextProvider holds a SINGLE dialog slot and close() is
  // indiscriminate, so track whether what's showing is still our backdrop.
  // Without this, closing "the loader" could just as easily close the error
  // dialog, or one the caller's onSuccess opened.
  const loaderIsOursRef = useRef(false);

  const closeOwnLoader = () => {
    if (!loaderIsOursRef.current) return;
    loaderIsOursRef.current = false;
    dialog.close();
  };

  const mutation = useMutation({
    mutationFn: async (mutationData: Object | void) => {
      const payload = { ...mutationData, ...params };
      try {
        const response = await api({
          method,
          url,
          ...(method.toLowerCase() === 'get' ? { params: payload } : { data: payload }),
          ...(includesAttachment ? {headers: { 'Content-Type': 'multipart/form-data' }} : {})
        });

        return response.data;
      } catch (error) {
        return Promise.reject(onErrorDefaultHandler(error));
      }
    },
    onSuccess: (data, variables, context) => {
      // Release the slot BEFORE handing control to the caller, so anything its
      // onSuccess opens is left alone.
      closeOwnLoader();
      if (onSuccess) onSuccess(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Hand the slot over rather than closing it: the error dialog replaces the
      // backdrop, and dropping ownership stops us closing the error dialog later.
      loaderIsOursRef.current = false;
      dialog.open(CasMutationErrorDialog, {
        title: errorTitle,
        mutation: { reset: mutation.reset, error },
        onDismiss: onErrorDismiss ? () => onErrorDismiss(error) : undefined,
      });
      if (onError) onError(error, variables, context);
    },
    ...rest,
  });

  // Open from an effect rather than during render.
  //
  // React Query awaits onSuccess before it dispatches the success state, so
  // isPending is still true while the caller's callback runs. A render-phase
  // `if (isPending) dialog.open(...)` therefore re-opened the backdrop that
  // onSuccess had just closed, whenever that callback updated state and its
  // re-render flushed before the dispatch - and nothing closed it again,
  // because that branch only ever opened. The result was a permanently dimmed,
  // unclickable page after a mutation that had actually succeeded (bug 15329).
  //
  // An effect keyed on isPending only runs when the flag genuinely changes, so
  // the re-entrant render in that race is a no-op.
  useEffect(() => {
    if (!showLoader || !mutation.isPending || loaderIsOursRef.current) return;
    loaderIsOursRef.current = true;
    dialog.open(CasLoaderWithBackdrop, {});
  }, [mutation.isPending, showLoader, dialog]);

  // Unmounting mid-flight (navigating away, closing a dialog that owns the
  // mutation) would otherwise strand the backdrop with no one left to close it.
  useEffect(
    () => () => closeOwnLoader(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return mutation;
}